import { supabase } from '../supabase';
import { validateWhatsApp } from '../phone-utils';

async function validateWhatsAppExists(whatsapp: string): Promise<{ exists: boolean; error?: string }> {
  const digits = whatsapp.replace(/\D/g, '');
  
  // Remove any +55 or 55 prefix if present
  const cleanDigits = digits.replace(/^55/, '');
  
  // Ensure we have exactly 11 digits after cleaning
  if (cleanDigits.length !== 11) {
    return { exists: false, error: 'Número de WhatsApp inválido' };
  }

  console.log('Checking user existence:', {
    originalInput: whatsapp,
    cleanDigits,
    timestamp: new Date().toISOString()
  });

  try {
    // First check user_profile table
    const { data: profileMatch, error: profileError } = await supabase
      .rpc('get_user_whatsapp', { user_id: auth.uid() });

    if (profileError) {
      console.error('Error checking user whatsapp:', profileError);
      return { exists: false, error: 'Erro ao verificar usuário' };
    }

    if (profileMatch === cleanDigits) {
      console.log('Found matching whatsapp:', {
        whatsapp: cleanDigits,
        timestamp: new Date().toISOString()
      });
      return { exists: true };
    }

    // If not found in profile, check auth.users email
    const { data: authUser, error: authError } = await supabase.auth
      .signInWithPassword({
        email: `${cleanDigits}@whatsapp.user`,
        password: 'dummy-password-for-check'
      });

    // We expect this to fail with invalid credentials, but if we get a user not found error,
    // then the user doesn't exist
    if (authError && authError.message.includes('Invalid login credentials')) {
      console.log('User exists in auth but not in profile');
      return { exists: true };
    }

    console.log('No user found with this WhatsApp number');
    return { exists: false };

  } catch (err) {
    console.error('Error in validateWhatsAppExists:', err);
    return { exists: false, error: 'Erro ao verificar usuário' };
  }
}

export async function requestPasswordReset(whatsapp: string) {
  if (!validateWhatsApp(whatsapp)) {
    throw new Error('Número de WhatsApp inválido');
  }

  const digits = whatsapp.replace(/\D/g, '');
  if (digits.length !== 11) {
    throw new Error('O número deve ter 11 dígitos (DDD + número)');
  }

  // Validate if WhatsApp exists in auth.users
  const validation = await validateWhatsAppExists(whatsapp);
  if (validation.error) {
    throw new Error(validation.error);
  }
  if (!validation.exists) {
    throw new Error('Número de WhatsApp não encontrado em nossa base de dados');
  }

  const formattedWhatsApp = digits;

  try {
    // Log the reset attempt
    await supabase.rpc('log_password_reset_attempt', {
      p_phone: formattedWhatsApp,
      p_success: true
    });

    // Call our edge function to send verification code
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-verification`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ whatsapp: formattedWhatsApp })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao enviar código de verificação');
    }
  } catch (err) {
    console.error('Error requesting password reset:', err);
    // Log the failed attempt
    await supabase.rpc('log_password_reset_attempt', {
      p_phone: formattedWhatsApp,
      p_success: false,
      p_error: err instanceof Error ? err.message : 'Unknown error'
    });
    throw new Error('Erro ao solicitar recuperação de senha. Por favor, tente novamente.');
  }
}

export async function verifyResetCode(whatsapp: string, code: string) {
  if (!code || code.length !== 6) {
    throw new Error('Código inválido');
  }

  const digits = whatsapp.replace(/\D/g, '');
  const formattedWhatsApp = digits;

  // Verify code exists and is valid
  const { data: codes, error: verifyError } = await supabase
    .from('verification_codes')
    .select()
    .eq('phone', formattedWhatsApp)
    .eq('code', code)
    .is('used_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(1);

  if (verifyError) {
    console.error('Error verifying code:', verifyError);
    throw new Error('Erro ao verificar código');
  }

  if (!codes || codes.length === 0) {
    throw new Error('Código inválido ou expirado');
  }

  // Mark code as used
  const { error: updateError } = await supabase
    .from('verification_codes')
    .update({ used_at: new Date().toISOString() })
    .eq('id', codes[0].id);

  if (updateError) {
    console.error('Error marking code as used:', updateError);
    throw new Error('Erro ao validar código');
  }
}

export async function resetPassword(whatsapp: string, code: string, newPassword: string) {
  if (newPassword.length < 6) {
    throw new Error('A senha deve ter no mínimo 6 caracteres');
  }

  // First verify the reset code
  await verifyResetCode(whatsapp, code);

  const digits = whatsapp.replace(/\D/g, '');
  const email = `${digits}@whatsapp.user`;

  try {
    // Call the reset-password edge function
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password: newPassword
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao redefinir senha');
    }

    // Redirect to login page
    window.location.href = '/';

  } catch (error) {
    console.error('Password reset error:', error);
    throw error instanceof Error ? error : new Error('Erro ao redefinir senha');
  }
}